import { makeMutable } from "react-native-reanimated";
import type { SharedValue } from "react-native-reanimated";
import {
  StyleSheet,
  UnistyleDependency,
  UnistylesRuntime,
} from "react-native-unistyles";

import type { Theme } from "$src/core/models/theme";

/**
 * A copy of the theme's data. `UnistylesRuntime.getTheme()` hands back the live
 * object that Unistyles keeps writing to, and Reanimated warns ("tried to modify
 * a key of an object which has been already passed to a worklet") once it has
 * serialized it. Helpers like `theme.spacing` are kept by reference — callers
 * read them off `.value` on the JS thread (see `SeekBar.styles.ts`).
 */
const snapshot = <T>(value: T): T => {
  if (typeof value === "function") return value;
  if (Array.isArray(value)) return value.map(snapshot) as T;
  if (value === null || typeof value !== "object") return value;

  return Object.fromEntries(
    Object.entries(value).map(([key, entry]) => [key, snapshot(entry)]),
  ) as T;
};

/**
 * One app-wide animated theme, instead of Unistyles' `useAnimatedTheme`, which
 * builds a fresh theme shareable and its own change listener per call site. On
 * a TV lane or an EPG grid that is one per card, for a value every card shares.
 *
 * Trade-off: this ignores `ScopedTheme`. The app has a single theme and does not
 * use it — reach for Unistyles' own hook if that ever changes.
 */
let animatedTheme: SharedValue<Theme> | undefined;

const createAnimatedTheme = () => {
  const theme = makeMutable(snapshot(UnistylesRuntime.getTheme()));

  StyleSheet.addChangeListener((changedDependencies) => {
    if (!changedDependencies.includes(UnistyleDependency.Theme)) {
      return;
    }
    theme.set(snapshot(UnistylesRuntime.getTheme()));
  });

  return theme;
};

const useAnimatedTheme = () => {
  animatedTheme ??= createAnimatedTheme();
  return animatedTheme;
};

export default useAnimatedTheme;
